import { Injectable, inject } from '@angular/core';
import {
  Observable,
  of,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  catchError,
} from 'rxjs';
import { GoodsSkuControllerService } from '@app/api/api/goodsSkuController.service';

export interface SKUAutocompleteItem {
  id: number;
  skuCode: string;
  skuName: string | null;
  spuCode: string;
  spuName: string;
  materialName: string;
  categoryName: string;
  status: number;
  costPrice: number;
  salesPrice: number;
  marketPrice: number;
  distributionPrice: number;
  wholesalePrice: number;
  clearingPrice: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  quantity?: number; // Added for table functionality
  category?: string; // Added for table functionality
}

export interface SKUAutocompleteResponse {
  pageContent: SKUAutocompleteItem[];
  totalElements: number;
  totalPages: number;
  empty: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SkuAutocompleteService {
  private goodsSkuController = inject(GoodsSkuControllerService);

  /**
   * Search SKUs by SKU Code
   */
  searchBySkuCode(
    skuCode: string,
    page: number = 0,
    size: number = 20
  ): Observable<SKUAutocompleteResponse> {
    if (!skuCode || skuCode.trim().length < 1) {
      return of({
        pageContent: [],
        totalElements: 0,
        totalPages: 0,
        empty: true,
      });
    }

    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag (ordinary SKUs)
        undefined, // categoryIdList
        undefined, // status
        undefined, // skuName
        skuCode.trim(), // skuCode
        page,
        size,
        undefined, // sortBy
        undefined // ascending
      )
      .pipe(
        map((response) => this.mapResponseToAutocomplete(response)),
        catchError((error) => {
          console.error('Error searching SKUs by code:', error);
          return of({
            pageContent: [],
            totalElements: 0,
            totalPages: 0,
            empty: true,
          });
        })
      );
  }

  /**
   * Search SKUs by SKU Name
   */
  searchBySkuName(
    skuName: string,
    page: number = 0,
    size: number = 20
  ): Observable<SKUAutocompleteResponse> {
    if (!skuName || skuName.trim().length < 1) {
      return of({
        pageContent: [],
        totalElements: 0,
        totalPages: 0,
        empty: true,
      });
    }

    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag (ordinary SKUs)
        undefined, // categoryIdList
        undefined, // status
        skuName.trim(), // skuName
        undefined, // skuCode
        page,
        size,
        undefined, // sortBy
        undefined // ascending
      )
      .pipe(
        map((response) => this.mapResponseToAutocomplete(response)),
        catchError((error) => {
          console.error('Error searching SKUs by name:', error);
          return of({
            pageContent: [],
            totalElements: 0,
            totalPages: 0,
            empty: true,
          });
        })
      );
  }

  /**
   * Search SKUs by both SKU Code and Name (AND condition)
   */
  searchBySkuCodeAndName(
    skuCode: string,
    skuName: string,
    page: number = 0,
    size: number = 20
  ): Observable<SKUAutocompleteResponse> {
    if (
      (!skuCode || skuCode.trim().length < 1) &&
      (!skuName || skuName.trim().length < 1)
    ) {
      return of({
        pageContent: [],
        totalElements: 0,
        totalPages: 0,
        empty: true,
      });
    }

    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag (ordinary SKUs)
        undefined, // categoryIdList
        undefined, // status
        skuName?.trim() || undefined, // skuName
        skuCode?.trim() || undefined, // skuCode
        page,
        size,
        undefined, // sortBy
        undefined // ascending
      )
      .pipe(
        map((response) => this.mapResponseToAutocomplete(response)),
        catchError((error) => {
          console.error('Error searching SKUs by code and name:', error);
          return of({
            pageContent: [],
            totalElements: 0,
            totalPages: 0,
            empty: true,
          });
        })
      );
  }

  /**
   * Get SKU details by ID
   */
  getSkuById(id: number): Observable<SKUAutocompleteItem | null> {
    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag (ordinary SKUs)
        undefined, // categoryIdList
        undefined, // status
        undefined, // skuName
        undefined, // skuCode
        0, // page
        1000, // size (large number to get all)
        undefined, // sortBy
        undefined // ascending
      )
      .pipe(
        map((response) => {
          const item = response.pageContent?.find((sku) => sku.id === id);
          return item ? this.mapItemToAutocomplete(item) : null;
        }),
        catchError((error) => {
          console.error('Error getting SKU by ID:', error);
          return of(null);
        })
      );
  }

  /**
   * Create debounced search observable for autocomplete
   */
  createDebouncedSearch(
    searchType: 'code' | 'name' | 'both',
    debounceMs: number = 300
  ): (query: string) => Observable<SKUAutocompleteItem[]> {
    return (query: string) => {
      if (!query || query.trim().length < 1) {
        return of([]);
      }

      const searchObservable =
        searchType === 'code'
          ? this.searchBySkuCode(query)
          : searchType === 'name'
          ? this.searchBySkuName(query)
          : this.searchBySkuCodeAndName(query, query);

      return searchObservable.pipe(
        debounceTime(debounceMs),
        distinctUntilChanged(),
        map((response) => response.pageContent || []),
        catchError(() => of([]))
      );
    };
  }

  /**
   * Map API response to autocomplete format
   */
  private mapResponseToAutocomplete(response: any): SKUAutocompleteResponse {
    return {
      pageContent: (response.pageContent || []).map((item: any) =>
        this.mapItemToAutocomplete(item)
      ),
      totalElements: response.totalElements || 0,
      totalPages: response.totalPages || 0,
      empty: response.empty || false,
    };
  }

  /**
   * Map individual SKU item to autocomplete format
   */
  private mapItemToAutocomplete(item: any): SKUAutocompleteItem {
    return {
      id: item.id,
      skuCode: item.skuCode || '',
      skuName: item.skuName || null,
      spuCode: item.spuCode || '',
      spuName: item.spuName || '',
      materialName: item.materialName || '',
      categoryName: item.categoryName || '',
      status: item.status || 0,
      costPrice: item.costPrice || 0,
      salesPrice: item.salesPrice || 0,
      marketPrice: item.marketPrice || 0,
      distributionPrice: item.distributionPrice || 0,
      wholesalePrice: item.wholesalePrice || 0,
      clearingPrice: item.clearingPrice || 0,
      weight: item.weight || 0,
      length: item.length || 0,
      width: item.width || 0,
      height: item.height || 0,
      quantity: 1, // Default quantity
      category: item.categoryName || '',
    };
  }

  /**
   * Get SKU items with pagination for table display
   */
  getSkuItems(
    page: number,
    size: number,
    searchTerm?: string
  ): Observable<any> {
    const params = {
      page: page.toString(),
      size: size.toString(),
      ...(searchTerm && { search: searchTerm }),
    };

    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag (ordinary SKUs)
        undefined, // categoryIdList
        undefined, // status
        searchTerm, // skuName
        undefined, // skuCode
        page,
        size,
        undefined, // sortBy
        undefined // ascending
      )
      .pipe(
        map((response) => ({
          content: (response.pageContent || []).map((item: any) =>
            this.mapItemToAutocomplete(item)
          ),
          totalElements: response.totalElements || 0,
          totalPages: response.totalPages || 0,
          empty: response.empty || false,
        })),
        catchError((error) => {
          console.error('Error fetching SKU items:', error);
          return of({
            content: [],
            totalElements: 0,
            totalPages: 0,
            empty: true,
          });
        })
      );
  }
}
